/**
 * Auto-Estimation Service
 * Automatically creates estimate entries when components are counted
 */

import { supabase } from '../lib/supabase';
import { queryLaborUnits } from './laborUnitsAgent';

export interface AutoEstimationResult {
  success: boolean;
  matched_component?: string;
  error?: string;
  // Detailed data for agent chat message
  componentName?: string;
  quantity?: number;
  laborData?: {
    normal_labor_units: number;
    difficult_labor_units: number;
    very_difficult_labor_units: number;
    unit: string;
    matched_component: string;
    measurement: string;
    section: string | null;
    page_number: number | null;
  };
  materialData?: {
    cost: number;
    title: string;
    url: string;
  };
}

/**
 * Get the next step number for estimate_labor_steps
 */
async function getNextStepNumber(estimationId: string): Promise<number> {
  const { data, error } = await supabase
    .from('estimate_labor_steps')
    .select('step_number')
    .eq('estimation_id', estimationId)
    .order('step_number', { ascending: false })
    .limit(1);

  if (error || !data || data.length === 0) {
    return 1;
  }

  return (data[0].step_number || 0) + 1;
}

/**
 * Scrape material cost for a component
 */
async function scrapeMaterialCost(componentName: string, matchedComponent?: string) {
  try {
    const searchQuery = matchedComponent || componentName;
    console.log('🔍 Scraping material cost for:', searchQuery);
    
    const response = await fetch('/api/scrape-material', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        searchQuery: searchQuery  // API expects 'searchQuery' not 'componentName'
      })
    });

    if (!response.ok) {
      console.warn('⚠️ Material scrape failed, continuing without material cost');
      return null;
    }

    const result = await response.json();
    
    // Extract the best_match data from the API response
    if (result.best_match) {
      return {
        cost: result.best_match.price,
        title: result.best_match.title,
        url: result.best_match.product_url
      };
    }
    
    return null;
  } catch (error) {
    console.warn('⚠️ Material scrape error:', error);
    return null;
  }
}

/**
 * Convert labor units to per-each (E) basis for counting
 * NECA uses: E = each, C = per 100, M = per 1000
 */
function convertLaborUnitsToEach(laborData: any): any {
  const converted = { ...laborData };
  
  if (laborData.unit === 'C') {
    // Convert from per-100 to per-each
    console.log('🔄 Converting labor units from C (per 100) to E (per each)');
    console.log(`   Before: ${laborData.normal_labor_units} hours per C`);
    converted.normal_labor_units = laborData.normal_labor_units ? laborData.normal_labor_units / 100 : 0;
    converted.difficult_labor_units = laborData.difficult_labor_units ? laborData.difficult_labor_units / 100 : 0;
    converted.very_difficult_labor_units = laborData.very_difficult_labor_units ? laborData.very_difficult_labor_units / 100 : 0;
    converted.unit = 'E';
    console.log(`   After: ${converted.normal_labor_units} hours per E`);
    console.log(`✅ Converted: ${laborData.normal_labor_units} per C → ${converted.normal_labor_units} per E`);
  } else if (laborData.unit === 'M') {
    // Convert from per-1000 to per-each
    console.log('🔄 Converting labor units from M (per 1000) to E (per each)');
    console.log(`   Before: ${laborData.normal_labor_units} hours per M`);
    converted.normal_labor_units = laborData.normal_labor_units ? laborData.normal_labor_units / 1000 : 0;
    converted.difficult_labor_units = laborData.difficult_labor_units ? laborData.difficult_labor_units / 1000 : 0;
    converted.very_difficult_labor_units = laborData.very_difficult_labor_units ? laborData.very_difficult_labor_units / 1000 : 0;
    converted.unit = 'E';
    console.log(`   After: ${converted.normal_labor_units} hours per E`);
    console.log(`✅ Converted: ${laborData.normal_labor_units} per M → ${converted.normal_labor_units} per E`);
  } else if (laborData.unit === 'E' || laborData.unit === 'EA') {
    // Already per-each, ensure unit is 'E' for consistency
    console.log('✅ Already in E/EA (per each) units, no conversion needed');
    console.log(`   Labor units: ${laborData.normal_labor_units} hours per each`);
    converted.unit = 'E';
  } else {
    // Unknown unit type - log warning but don't convert
    console.warn(`⚠️ Unknown unit type: ${laborData.unit} - treating as per-each`);
    converted.unit = 'E';
  }
  
  return converted;
}

/**
 * Automatically estimate a component and add to estimate table
 */
export async function autoEstimateComponent(
  componentName: string,
  quantity: number,
  estimationId: string,
  onProgress?: (message: string) => void
): Promise<AutoEstimationResult> {
  try {
    console.log('🤖 Auto-estimating component:', { componentName, quantity, estimationId });
    
    // Step 1: Check if component already exists with complete data
    onProgress?.('Checking existing estimates...');
    const { data: existingRows } = await supabase
      .from('estimate_labor_steps')
      .select('*')
      .eq('estimation_id', estimationId)
      .or(`step_description.ilike.%${componentName}%,matched_component.ilike.%${componentName}%`);

    const existing = existingRows?.[0];

    if (existing) {
      // Check if existing row has complete labor and material data
      const hasCompleteLaborData = existing.normal_labor_units !== null && 
                                   existing.normal_labor_units !== undefined;
      const hasMaterialData = existing.material_cost !== null && 
                             existing.material_cost !== undefined;

      if (hasCompleteLaborData && hasMaterialData) {
        console.log('✅ Component already has complete data, just updating quantity');
        onProgress?.('Updating quantity...');
        
        const { error: updateError } = await supabase
          .from('estimate_labor_steps')
          .update({ quantity: existing.quantity + quantity })
          .eq('id', existing.id);

        if (updateError) {
          console.error('❌ Failed to update quantity:', updateError);
          return { success: false, error: 'Failed to update quantity' };
        }

        console.log(`✅ Updated ${existing.matched_component} quantity: ${existing.quantity} → ${existing.quantity + quantity}`);
        onProgress?.('Updated quantity!');
        
        // NEW: Sync existing data to library (in case it's not there yet)
        const { data: estData } = await supabase
          .from('estimations')
          .select('user_id')
          .eq('id', estimationId)
          .single();
        
        if (estData?.user_id) {
          try {
            const { componentLibraryService } = await import('./componentLibraryService');
            await componentLibraryService.syncPricingData(estData.user_id, componentName, {
              material_cost: existing.material_cost,
              labor_unit_type: existing.unit as 'E' | 'C' | 'M',
              labor_units: {
                normal: existing.normal_labor_units,
                difficult: existing.difficult_labor_units,
                very_difficult: existing.very_difficult_labor_units
              }
            });
            console.log(`💾 Synced existing data to library for "${componentName}"`);
          } catch (syncError) {
            console.error('Failed to sync to library (non-critical):', syncError);
          }
        }
        
        return { 
          success: true, 
          matched_component: existing.matched_component,
          componentName,
          quantity,
          laborData: {
            normal_labor_units: existing.normal_labor_units,
            difficult_labor_units: existing.difficult_labor_units,
            very_difficult_labor_units: existing.very_difficult_labor_units,
            unit: existing.unit,
            matched_component: existing.matched_component,
            measurement: existing.measurement,
            section: existing.section,
            page_number: existing.page_number
          },
          materialData: existing.material_cost ? {
            cost: existing.material_cost,
            title: existing.material_product_title || '',
            url: existing.material_product_url || ''
          } : undefined
        };
      } else {
        console.log('⚠️ Component exists but has incomplete data, will update with new data');
      }
    }

    // Step 2: Check user's component library FIRST before expensive NECA lookup
    onProgress?.('Checking your component library...');
    
    // Get user_id from estimation
    const { data: estData } = await supabase
      .from('estimations')
      .select('user_id')
      .eq('id', estimationId)
      .single();
    
    let laborDataFromLibrary = null;
    let customLaborHoursFromLibrary = null;
    
    if (estData?.user_id) {
      console.log('🔍 Checking component library for:', componentName);
      const { componentLibraryService } = await import('./componentLibraryService');
      const library = await componentLibraryService.getUserLibrary(estData.user_id);
      
      console.log('📚 Library has', library.length, 'components');
      
      const libComponent = library.find((c: any) => 
        c.component_name.toLowerCase() === componentName.toLowerCase()
      );
      
      if (libComponent) {
        console.log('✅ Found component in library:', {
          name: libComponent.component_name,
          has_labor_units: !!libComponent.labor_units,
          labor_units: libComponent.labor_units,
          custom_hours: libComponent.custom_labor_hours
        });
      } else {
        console.log('❌ Component not found in library. Searched for:', componentName);
        console.log('   Library components:', library.map((c: any) => c.component_name).slice(0, 10));
      }
      
      if (libComponent?.labor_units && 
          (libComponent.labor_units.normal > 0 || libComponent.labor_units.difficult > 0)) {
        console.log('✅ Using labor units from library!');
        onProgress?.('Using saved labor units from library!');
        
        // Check if there's a custom labor hours override (PREFERRED VALUE)
        if (libComponent.custom_labor_hours !== null && libComponent.custom_labor_hours !== undefined) {
          customLaborHoursFromLibrary = libComponent.custom_labor_hours;
          console.log('⭐ Found custom labor hours in library (PREFERRED):', customLaborHoursFromLibrary);
        }
        
        // Use library data instead of NECA lookup
        laborDataFromLibrary = {
          matched_component: libComponent.component_name,  // Use library name (might be uppercase)
          normal_labor_units: libComponent.labor_units.normal,
          difficult_labor_units: libComponent.labor_units.difficult,
          very_difficult_labor_units: libComponent.labor_units.very_difficult,
          unit: libComponent.labor_unit_type || 'E',
          measurement: '',
          section: 'From Library',
          page_number: null,
          quantity: quantity
        };
      } else {
        if (libComponent) {
          console.log('⚠️ Component in library but missing labor units:', libComponent.labor_units);
        }
        console.log('⚠️ Will query NECA');
      }
    }
    
    // Step 3: Query NECA for labor units (ONLY if not in library)
    let necaResult;
    
    if (laborDataFromLibrary) {
      console.log('✅ Skipping NECA lookup - using library data');
      necaResult = { steps: [laborDataFromLibrary] };
    } else {
      onProgress?.('Looking up labor units in NECA manual...');
      try {
        // Use lightweight mode - skip context gathering and RSMeans (just NECA + materials)
        necaResult = await queryLaborUnits(
          `${quantity} ${componentName}`,
          (step) => {
            if (step.status === 'starting' || step.status === 'completed') {
              onProgress?.(step.step);
            }
          },
          estimationId,
          undefined, // No context needed for simple component counting
          false // fullEstimate = false (lightweight mode)
        );

        console.log('✅ NECA lookup complete:', necaResult);
      } catch (necaError) {
        console.error('❌ NECA lookup failed:', necaError);
        onProgress?.('Failed to find labor units');
        
        // Continue without NECA data - user can fill in manually
        return {
          success: false,
          error: 'NECA lookup failed'
        };
      }
    }

    // Extract first step (assuming single component)
    const firstStep = necaResult.steps?.[0];
    if (!firstStep) {
      console.warn('⚠️ No labor data found in result');
      return {
        success: false,
        error: 'No labor data found'
      };
    }

    // Log what we have BEFORE conversion
    console.log('📥 Labor data (BEFORE conversion):', {
      neca_matched: firstStep.matched_component,
      normal_labor_units: firstStep.normal_labor_units,
      unit: firstStep.unit,
      quantity: firstStep.quantity,
      source: laborDataFromLibrary ? 'Library' : 'NECA'
    });

    // Convert labor units to per-each if needed
    const laborData = convertLaborUnitsToEach(firstStep);
    
    // IMPORTANT: Use user's original component name, not NECA's matched name
    const necaMatchedName = laborData.matched_component; // Keep for reference
    const matchedComponent = componentName; // Use user's name instead
    
    // Override matched_component to use user's original name
    laborData.matched_component = componentName;
    
    // Log what we're actually using
    console.log('🔄 After conversion and name override:', {
      user_name: componentName,
      neca_matched: necaMatchedName,
      normal_labor_units: laborData.normal_labor_units,
      unit: laborData.unit,
      using_name: matchedComponent
    });

    // Step 4: Re-check if component was added while we were doing lookup
    const { data: reCheckExisting } = await supabase
      .from('estimate_labor_steps')
      .select('*')
      .eq('estimation_id', estimationId)
      .eq('matched_component', matchedComponent)
      .maybeSingle();

    if (reCheckExisting) {
      console.log('📝 Component was added during lookup, updating instead of inserting...');
      onProgress?.('Updating existing estimate...');
      
      // Update existing row with labor data and new quantity
      const { error: updateError } = await supabase
        .from('estimate_labor_steps')
        .update({
          quantity: reCheckExisting.quantity + quantity,
          normal_labor_units: laborData.normal_labor_units,
          difficult_labor_units: laborData.difficult_labor_units,
          very_difficult_labor_units: laborData.very_difficult_labor_units,
          unit: laborData.unit,
          measurement: laborData.measurement,
          section: necaMatchedName || laborData.section,  // Store NECA match for reference
          page_number: laborData.page_number
        })
        .eq('id', reCheckExisting.id);

      if (updateError) {
        console.error('❌ Failed to update estimate:', updateError);
        return { success: false, error: 'Failed to update estimate' };
      }

      console.log(`✅ Updated ${matchedComponent} with labor data and quantity`);
    }

    // Step 5: Scrape material cost (optional, don't block on failure)
    // Check library first for material cost too
    let materialData = null;
    if (estData?.user_id && laborDataFromLibrary) {
      const { componentLibraryService } = await import('./componentLibraryService');
      const library = await componentLibraryService.getUserLibrary(estData.user_id);
      const libComponent = library.find((c: any) => 
        c.component_name.toLowerCase() === componentName.toLowerCase()
      );
      
      if (libComponent?.material_cost) {
        console.log('✅ Using material cost from library:', libComponent.material_cost);
        materialData = {
          cost: libComponent.material_cost,
          title: componentName,
          url: ''
        };
      }
    }
    
    if (!materialData) {
      onProgress?.('Finding material costs...');
      materialData = await scrapeMaterialCost(componentName, matchedComponent);
    }

    // Step 6: Insert or update estimate row
    if (reCheckExisting) {
      // Already updated above, just add material cost if we have it
      if (materialData?.cost) {
        onProgress?.('Updating material costs...');
        await supabase
          .from('estimate_labor_steps')
          .update({
            material_cost: materialData.cost,
            material_product_title: materialData.title,
            material_product_url: materialData.url
          })
          .eq('id', reCheckExisting.id);
      }
      
      console.log('✅ Auto-estimation complete (updated existing):', matchedComponent);
      onProgress?.('Updated estimate!');
      
      // Save matched component back to key for future correlation
      await saveMatchedComponentToKey(estimationId, componentName, matchedComponent);
      
      // NEW: Sync to rolling key library
      if (estData?.user_id) {
        try {
          const { componentLibraryService } = await import('./componentLibraryService');
          await componentLibraryService.syncPricingData(estData.user_id, componentName, {
            material_cost: materialData?.cost,
            labor_unit_type: laborData.unit as 'E' | 'C' | 'M',
            labor_units: {
              normal: laborData.normal_labor_units,
              difficult: laborData.difficult_labor_units,
              very_difficult: laborData.very_difficult_labor_units
            },
            custom_labor_hours: customLaborHoursFromLibrary
          });
          console.log(`💾 Synced updated estimation data to library for "${componentName}"`);
        } catch (syncError) {
          console.error('Failed to sync to library (non-critical):', syncError);
        }
      }
      
      return { 
        success: true, 
        matched_component: matchedComponent,
        componentName,
        quantity,
        laborData: {
          normal_labor_units: laborData.normal_labor_units,
          difficult_labor_units: laborData.difficult_labor_units,
          very_difficult_labor_units: laborData.very_difficult_labor_units,
          unit: laborData.unit,
          matched_component: laborData.matched_component,
          measurement: laborData.measurement,
          section: laborData.section,
          page_number: laborData.page_number
        },
        materialData: materialData || undefined
      };
    } else {
      // Insert new row into estimate table
      onProgress?.('Adding to estimate...');
      const stepNumber = await getNextStepNumber(estimationId);

      const { error: insertError } = await supabase
        .from('estimate_labor_steps')
        .insert({
        estimation_id: estimationId,
        step_number: stepNumber,
        step_description: componentName,
        quantity: quantity,
        quantity_unit: laborData.unit || 'E',
        matched_component: matchedComponent,  // User's original name
        measurement: laborData.measurement || '',
        normal_labor_units: laborData.normal_labor_units || 0,
        difficult_labor_units: laborData.difficult_labor_units || 0,
        very_difficult_labor_units: laborData.very_difficult_labor_units || 0,
        unit: laborData.unit || 'E',
        section: necaMatchedName || laborData.section || '',  // Store NECA match in section for reference
        page_number: laborData.page_number || null,
        material_cost: materialData?.cost || null,
        material_product_title: materialData?.title || null,
        material_product_url: materialData?.url || null,
        labor_hours_override: customLaborHoursFromLibrary || null  // Use custom hours from library if exists
      });

      if (insertError) {
        console.error('❌ Failed to insert into estimate table:', insertError);
        return { success: false, error: 'Failed to add to estimate' };
      }

      console.log('✅ Auto-estimation complete (new row):', matchedComponent);
      onProgress?.('Added to estimate!');
      
      // Save matched component back to key for future correlation
      await saveMatchedComponentToKey(estimationId, componentName, matchedComponent);
      
      // NEW: Sync to rolling key library
      if (estData?.user_id) {
        try {
          const { componentLibraryService } = await import('./componentLibraryService');
          await componentLibraryService.syncPricingData(estData.user_id, componentName, {
            material_cost: materialData?.cost,
            labor_unit_type: laborData.unit as 'E' | 'C' | 'M',
            labor_units: {
              normal: laborData.normal_labor_units,
              difficult: laborData.difficult_labor_units,
              very_difficult: laborData.very_difficult_labor_units
            },
            custom_labor_hours: customLaborHoursFromLibrary
          });
          console.log(`💾 Synced auto-estimation data to library for "${componentName}"`);
        } catch (syncError) {
          console.error('Failed to sync to library (non-critical):', syncError);
        }
      }
      
      return { 
        success: true, 
        matched_component: matchedComponent,
        componentName,
        quantity,
        laborData: {
          normal_labor_units: laborData.normal_labor_units,
          difficult_labor_units: laborData.difficult_labor_units,
          very_difficult_labor_units: laborData.very_difficult_labor_units,
          unit: laborData.unit,
          matched_component: laborData.matched_component,
          measurement: laborData.measurement,
          section: laborData.section,
          page_number: laborData.page_number
        },
        materialData: materialData || undefined
      };
    }

  } catch (error) {
    console.error('❌ Auto-estimation failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Save matched component name back to key for future correlation
 */
async function saveMatchedComponentToKey(
  estimationId: string,
  componentName: string,
  matchedComponent: string
): Promise<void> {
  try {
    console.log(`🔗 Saving matched component correlation: "${componentName}" → "${matchedComponent}"`);
    
    const { data, error: fetchError } = await supabase
      .from('estimations')
      .select('project_key')
      .eq('id', estimationId)
      .single();
    
    if (fetchError || !data?.project_key) {
      console.warn('⚠️ Could not fetch key to save correlation');
      return;
    }
    
    const key = data.project_key;
    const component = key.components?.find((c: any) => 
      c.name.toLowerCase() === componentName.toLowerCase()
    );
    
    if (component) {
      component.estimate_matched_component = matchedComponent;
      
      const { error: updateError } = await supabase
        .from('estimations')
        .update({ project_key: key })
        .eq('id', estimationId);
      
      if (updateError) {
        console.error('❌ Failed to save correlation:', updateError);
      } else {
        console.log(`✅ Saved correlation to key`);
      }
    }
  } catch (error) {
    console.error('❌ Error saving correlation:', error);
  }
}

/**
 * Update estimate quantity when component count changes
 */
export async function updateEstimateQuantity(
  estimationId: string,
  componentName: string,
  newQuantity: number,
  matchedComponent?: string
): Promise<boolean> {
  try {
    console.log(`📊 Updating estimate quantity for "${componentName}" to ${newQuantity}`);
    
    // If no matched component provided, try to fetch from key
    let matchedComponentToUse = matchedComponent;
    if (!matchedComponentToUse) {
      const { data: estimation } = await supabase
        .from('estimations')
        .select('project_key')
        .eq('id', estimationId)
        .single();
      
      if (estimation?.project_key?.components) {
        const keyComponent = estimation.project_key.components.find((c: any) =>
          c.name.toLowerCase() === componentName.toLowerCase()
        );
        matchedComponentToUse = keyComponent?.estimate_matched_component;
        
        if (matchedComponentToUse) {
          console.log(`🔗 Found saved correlation in key: "${matchedComponentToUse}"`);
        }
      }
    } else {
      console.log(`🔗 Using provided correlation: "${matchedComponentToUse}"`);
    }

    // Strategy 1: Use matched_component if available (most accurate)
    console.log(`🔍 Strategy 1: Searching for matched component "${matchedComponentToUse || 'none'}"`);
    let { data, error } = matchedComponentToUse ? await supabase
      .from('estimate_labor_steps')
      .select('*')
      .eq('estimation_id', estimationId)
      .eq('matched_component', matchedComponentToUse)
    : { data: null, error: null };

    // Strategy 2: Fallback to partial name search
    if (!data || data.length === 0) {
      console.log(`⚠️ Strategy 1 failed (no match for "${matchedComponentToUse || 'none'}")`);
      console.log(`🔍 Strategy 2: Trying partial name search for "${componentName}"`);
      const result = await supabase
        .from('estimate_labor_steps')
        .select('*')
        .eq('estimation_id', estimationId)
        .or(`step_description.ilike.%${componentName}%,matched_component.ilike.%${componentName}%`);
      
      data = result.data;
      error = result.error;
      
      if (data && data.length > 0) {
        console.log(`✅ Strategy 2 found ${data.length} match(es)`);
      } else {
        console.log(`❌ Strategy 2 also failed`);
      }
    } else {
      console.log(`✅ Strategy 1 found ${data.length} match(es)`);
    }

    if (error) {
      console.error('❌ Failed to find estimate row:', error);
      return false;
    }

    if (!data || data.length === 0) {
      console.warn('⚠️ ============================================');
      console.warn(`⚠️ NO ESTIMATE ROW FOUND FOR: "${componentName}"`);
      console.log(`   Searched for: ${matchedComponentToUse ? `"${matchedComponentToUse}"` : 'partial match'}`);
      console.log(`   In estimation: ${estimationId}`);
      console.log('💡 Component not yet estimated - will be estimated on next autocount');
      console.warn('⚠️ ============================================');
      return false;
    }

    // Update the first matching row
    const rowToUpdate = data[0];
    
    // Check if quantity actually changed
    if (rowToUpdate.quantity === newQuantity) {
      console.log(`ℹ️ Quantity unchanged for ${componentName}, skipping update`);
      return true;
    }

    const { error: updateError } = await supabase
      .from('estimate_labor_steps')
      .update({ quantity: newQuantity })
      .eq('id', rowToUpdate.id);

    if (updateError) {
      console.error('❌ Failed to update estimate quantity:', updateError);
      return false;
    }

    console.log(`✅ Updated estimate quantity for ${componentName}: ${rowToUpdate.quantity} → ${newQuantity}`);
    return true;

  } catch (error) {
    console.error('❌ Error updating estimate quantity:', error);
    return false;
  }
}
